import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';

/**
 * TrustRadar Component
 * Dashboard with 4 key metric cards
 */
export const TrustRadar = ({ metrics }) => {
  const { t } = useTranslation(['dashboard']);

  if (!metrics) {
    return (
      <Typography color="textSecondary">{t('dashboard:trustRadar.loading')}</Typography>
    );
  }

  const { complianceIndex, totalVendors, avgScore, dpaComplianceRate, dpaSigned } = metrics;

  const getScoreColor = (score) => {
    if (score >= 90) return 'success.main';
    if (score >= 70) return 'warning.main';
    return 'error.main';
  };

  const metricCards = [
    {
      title: t('dashboard:trustRadar.complianceIndex'),
      value: `${complianceIndex?.toFixed(1) || 0}%`,
      icon: <TrendingUpIcon fontSize="large" />,
      color: getScoreColor(complianceIndex),
      description: t('dashboard:trustRadar.complianceIndexDesc'),
      progress: complianceIndex
    },
    {
      title: t('dashboard:trustRadar.totalVendors'),
      value: totalVendors || 0,
      icon: <BusinessIcon fontSize="large" />,
      color: 'primary.main',
      description: t('dashboard:trustRadar.totalVendorsDesc'),
      breakdown: metrics.vendorsByTier ? t('dashboard:trustRadar.tierBreakdown', { tier1: metrics.vendorsByTier.tier1 || 0, tier2: metrics.vendorsByTier.tier2 || 0, tier3: metrics.vendorsByTier.tier3 || 0 }) : null
    },
    {
      title: t('dashboard:trustRadar.avgScore'),
      value: `${avgScore?.toFixed(1) || 0}%`,
      icon: <AssessmentIcon fontSize="large" />,
      color: getScoreColor(avgScore),
      description: t('dashboard:trustRadar.avgScoreDesc'),
      progress: avgScore
    },
    {
      title: t('dashboard:trustRadar.dpaRate'),
      value: `${dpaSigned || 0}/${totalVendors || 0}`,
      icon: <VerifiedUserIcon fontSize="large" />,
      color: dpaComplianceRate >= 80 ? 'success.main' : 'warning.main',
      description: t('dashboard:trustRadar.dpaRateDesc'),
      progress: dpaComplianceRate
    }
  ];

  return (
    <Grid container spacing={3}>
      {metricCards.map((metric, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="h6" component="div" fontSize="0.875rem" color="textSecondary">
                  {metric.title}
                </Typography>
                <Box sx={{ color: metric.color }}>
                  {metric.icon}
                </Box>
              </Box>

              <Typography variant="h3" component="div" fontWeight="bold" sx={{ color: metric.color, mb: 1 }}>
                {metric.value}
              </Typography>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {metric.description}
              </Typography>

              {metric.progress !== undefined && (
                <LinearProgress
                  variant="determinate"
                  value={metric.progress}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: metric.color
                    }
                  }}
                />
              )}

              {metric.breakdown && (
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                  {metric.breakdown}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};
